import { useToggle } from '@tonic-one/react-hooks';
import {
  Alert,
  AlertCloseButton,
  Collapse,
  Flex,
  Link,
  Text,
} from '@tonic-one/react';
import { Trans, useTranslation } from 'react-i18next';
import { useLicenseStore } from '@Models/useLicenseModel';
import dayjs from 'dayjs';
import { THIRD_PARTY_LINK } from '@Utils/casDocLink';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import { IS_UAE } from '@Utils/const';

interface LicenseInfo {
  tenantId: string;
  enabled: boolean;
  autoRenew: number;
  expirationDate: string;
  graceEndDate: string;
  lastModifiedDate: string;
  seats: number;
  status: string;
  type: string;
  salesType: string;
}

const AlertBanner = ({ children }: any) => {
  const [isOpen, toggleIsOpen] = useToggle(true);
  const onClose = () => toggleIsOpen(false);

  return (
    <Collapse in={isOpen} unmountOnExit>
      <Alert variant="outline" severity="info" onClose={onClose} mt={8}>
        <Flex alignItems="center" justifyContent="space-between" mr="10x">
          <Text>{children}</Text>
        </Flex>
        <AlertCloseButton
          top={3}
          right={7}
          position="absolute"
          data-test="alert-close-button"
        />
      </Alert>
    </Collapse>
  );
};

type LicenseType = 'CAS' | 'CAS-SF' | 'TMCAS-EMAIL-ADDON';

const LicenseExpiredBanner = () => {
  const { t } = useTranslation();
  const { offset } = useUtcOffsetStore();
  const { license, hasCasLicense, hasSalesforceLicense, hasEmailAddOnLicense } =
    useLicenseStore();
  const licenseTypes: LicenseType[] = [];
  if (hasCasLicense) {
    licenseTypes.push('CAS');
  }
  if (hasSalesforceLicense) {
    licenseTypes.push('CAS-SF');
  }
  if (hasEmailAddOnLicense) {
    licenseTypes.push('TMCAS-EMAIL-ADDON');
  }
  return (
    <>
      {licenseTypes.map((licenseType) => {
        const licenseInfo = license[licenseType] as LicenseInfo;
        if (!licenseInfo) {
          return null;
        }
        const currentDate = dayjs();
        const expiringDate = dayjs(licenseInfo.expirationDate).subtract(
          30,
          'day'
        );

        const expiredDate = dayjs(licenseInfo.expirationDate);
        const graceExpiredDate = dayjs(licenseInfo.graceEndDate);
        const autoRenew = !!licenseInfo.autoRenew;

        if (
          currentDate.isAfter(expiringDate) &&
          currentDate.isBefore(expiredDate)
        ) {
          return (
            !autoRenew && (
              <AlertBanner>
                <Trans
                  i18nKey="common.serverErrors.license.trialPeriod"
                  values={{
                    product: t(`common.productName.${licenseType}`),
                    days: expiredDate.diff(currentDate, 'day'),
                    date: dayjs(expiredDate)
                      .utcOffset(offset)
                      .format(t('common.momentShortDateFormat')),
                  }}
                >
                  placeholder
                  {!IS_UAE ? (
                    <Link
                      href={THIRD_PARTY_LINK.contactReseller}
                      target="_blank"
                    ></Link>
                  ) : (
                    <></>
                  )}
                </Trans>
              </AlertBanner>
            )
          );
        } else if (
          currentDate.isAfter(expiredDate) &&
          currentDate.isBefore(graceExpiredDate)
        ) {
          return (
            !autoRenew && (
              <AlertBanner>
                <Trans
                  i18nKey="common.serverErrors.license.gracePeriod"
                  values={{
                    product: t(`common.productName.${licenseType}`),
                    date: dayjs(expiredDate)
                      .utcOffset(offset)
                      .format(t('common.momentShortDateFormat')),
                    days: graceExpiredDate.diff(currentDate, 'day'),
                  }}
                >
                  placeholder
                  {!IS_UAE ? (
                    <Link
                      href={THIRD_PARTY_LINK.contactReseller}
                      target="_blank"
                    ></Link>
                  ) : (
                    <></>
                  )}
                </Trans>
              </AlertBanner>
            )
          );
        }
      })}
    </>
  );
};

export default LicenseExpiredBanner;
